import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import BroadcastFullscreenIcon from "../../../assets/icons/broadcast-fullscreen.svg";
import CloseIcon from "../../../assets/icons/close.svg";
import VideoLiveReplaysPlayer, { VideoLiveReplaysPlayerRef } from "../../../components/VideoLiveReplaysPlayer/VideoLiveReplaysPlayer";
import { authService, liveService } from "../../../services";
import HighlightMomentItem from "./components/HighlightMomentItem";
import "./index.less";

const LiveReplayDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [isLandscape, setIsLandscape] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [loading, setLoading] = useState(true);
  const [replayData, setReplayData] = useState<any>(null);
  const videoPlayerRef = useRef<VideoLiveReplaysPlayerRef>(null);
  const hasFetchedRef = useRef(false);
  const streamThumbnail = location.state?.thumbnail;

  // Detect landscape orientation
  useEffect(() => {
    const handleOrientationChange = () => {
      const landscape = window.matchMedia("(orientation: landscape)").matches;
      setIsLandscape(landscape);
    };

    // Initial check
    handleOrientationChange();

    // Listen for orientation changes
    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  // Fetch broadcast data
  useEffect(() => {
    if (!id || hasFetchedRef.current) return;

    const fetchBroadcastData = async () => {
      try {
        setLoading(true);
        const data = await liveService.getBroadcastById(id);
        setReplayData(data);
      } catch (error) {
        console.error("Error fetching broadcast data:", error);
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    hasFetchedRef.current = true;
    fetchBroadcastData();
  }, [id, navigate]);

  const handleClose = async () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      await document.exitFullscreen();
    }

    // Redirect to creator page if user is creator, otherwise to fan page
    const userRole = authService.getRole();
    if (userRole === "creator") {
      navigate("/creator/live-replays");
    } else {
      navigate("/p/fan/lives?tab=replays");
    }
  };

  const handleVideoFullscreen = async () => {
    const videoElement = document.querySelector(".replay-video") as HTMLVideoElement;

    if (!videoElement) return;

    try {
      if (videoElement.requestFullscreen) {
        await videoElement.requestFullscreen();
      } else if ((videoElement as any).webkitRequestFullscreen) {
        await (videoElement as any).webkitRequestFullscreen();
      } else if ((videoElement as any).webkitEnterFullscreen) {
        // iOS Safari specific
        await (videoElement as any).webkitEnterFullscreen();
      }
    } catch (error) {
      console.log("Video fullscreen failed:", error);
    }
  };

  const handleHighlightClick = async (timestamp: number | string, highlightId: string) => {
    const time = typeof timestamp === "string" ? parseFloat(timestamp) : timestamp;
    if (videoPlayerRef.current && !isNaN(time) && isFinite(time)) {
      videoPlayerRef.current.seekTo(time);

      // Mark highlight as watched
      try {
        await liveService.markHighlightAsWatched(highlightId);

        // Update local state to reflect the change
        setReplayData((prevData: any) => ({
          ...prevData,
          highlights: prevData.highlights.map((h: any) => (h._id === highlightId ? { ...h, watched: true } : h)),
        }));
      } catch (error) {
        console.error("Failed to mark highlight as watched:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="live-replay-detail-view" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!replayData) {
    return (
      <div className="live-replay-detail-view" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div>No replay data found</div>
      </div>
    );
  }

  return (
    <div className="live-replay-detail-view">
      <div className={`replay-main-container ${isLandscape ? "landscape" : ""}`}>
        <div className="replay-video-section">
          <VideoLiveReplaysPlayer
            videoUrl={replayData.recordingUrl}
            ref={videoPlayerRef}
            onFullscreen={handleVideoFullscreen}
            onControlsVisibilityChange={setShowControls}
          />

          <div
            className="replay-video-header"
            style={{
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? "auto" : "none",
            }}
          >
            <div className="header-right">
              <img src={CloseIcon} width={22} alt="close" onClick={handleClose} style={{ cursor: "pointer" }} />
            </div>
          </div>

          <div
            className="replay-fullscreen-icon"
            style={{
              opacity: showControls ? 1 : 0,
              pointerEvents: showControls ? "auto" : "none",
            }}
          >
            <img src={BroadcastFullscreenIcon} width={24} alt="fullscreen" onClick={handleVideoFullscreen} style={{ cursor: "pointer" }} />
          </div>
        </div>

        <div className="replay-highlights-section">
          <h2 className="highlights-title">{t("highlights")}</h2>
          {replayData.highlights && replayData.highlights.length > 0 ? (
            <div className="highlights-list">
              {replayData.highlights.map((highlight: any, index: number) => (
                <HighlightMomentItem
                  key={highlight.id}
                  thumbnail={streamThumbnail || replayData.scheduleData?.media?.absolutePath || highlight.thumbnail}
                  number={index + 1}
                  watched={highlight.watched}
                  duration={highlight.duration}
                  time={highlight.time}
                  onClick={() => handleHighlightClick(highlight.time, highlight._id)}
                />
              ))}
            </div>
          ) : (
            <div className="no-highlights-message">
              <p>{t("no-highlights-message")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveReplayDetailPage;
